import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import Topbar from "@/components/Topbar";
import ContactSection from "@/components/ContactSection";
import {
  ArrowRight, Sparkle, Robot, ShieldCheck, HardHat, Briefcase,
  ClipboardText, Handshake, Clock, FilePdf,
  Compass, CurrencyDollar, Waveform, Trophy, LockKey,
} from "@phosphor-icons/react";

/**
 * v3.2.20 · Landing page redesign.
 * Two-sided marketplace narrative (Client & PE) with the AI Proposal
 * Leaderboard as the hero differentiator, followed by dual workflow,
 * technical tool proof, and trust/compliance signals.
 * Adheres to /app/design_guidelines.json (industrial-cyan · dark
 * · Swiss Brutalist / Technical archetype).
 */

const HERO_BG =
  "https://images.pexels.com/photos/19895882/pexels-photo-19895882.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const CLIENT_STEPS = [
  { icon: ClipboardText, tag: "01 · SCOPE",       title: "Post a job",         copy: "Describe the panel, upload specs, set a budget in USD or your local currency." },
  { icon: Robot,         tag: "02 · AI RANK",     title: "AI ranks proposals", copy: "Claude 4.6 scores every incoming bid with diff-style commentary and a weighted composite." },
  { icon: Handshake,     tag: "03 · AWARD",       title: "Award the engineer", copy: "Compare itemized scope side-by-side, pick your PE, and lock the engagement." },
  { icon: LockKey,       tag: "04 · MILESTONES",  title: "Approve & release",  copy: "Files auto-submit against milestones. Approve to release payment. 7-day safety net." },
];

const PE_STEPS = [
  { icon: Compass,       tag: "01 · MATCH",       title: "Save a search",       copy: "Filter open jobs by country, region, and budget. Get weekly digest emails when new jobs match." },
  { icon: FilePdf,       tag: "02 · BID",         title: "Itemized proposal",   copy: "Hours by Jr / Sr rate auto-cost your scope. Export a stamped PDF in one click." },
  { icon: Waveform,      tag: "03 · DELIVER",     title: "Upload deliverables", copy: "Drop MS1_walkdown.pdf and the milestone auto-flips to `submitted`. Countdown widget shows time left." },
  { icon: CurrencyDollar, tag: "04 · GET PAID",   title: "Milestone released",  copy: "Client approves in-app. Payment is marked due. No approval in 7d? Auto-release fires." },
];

const TRUST = [
  { icon: ShieldCheck, title: "License-verified engineers",
    copy: "Every PE is admin-approved. Expiry dates are tracked and auto-suspend triggers when a license lapses." },
  { icon: LockKey, title: "Milestone-lock payment protection",
    copy: "Deliverables submitted against milestones stay locked until you approve, reject, or the 7-day auto-release fires." },
  { icon: FilePdf, title: "Stamped, downloadable PDFs",
    copy: "Every proposal and deliverable exports as an audit-friendly PDF with revision history." },
  { icon: CurrencyDollar, title: "Dual-currency, live FX",
    copy: "Post in USD, we render in your local currency at live rates. Awarded proposals lock the FX so bids don't move." },
];

/* ─── AI Leaderboard mockup (static, feature showcase) ─────────── */

function LeaderRow({ rank, name, score, delta, comment, top }) {
  const bg = top ? "bg-[#00E5FF]/5 border-l-2 border-l-[#00E5FF]" : "border-l-2 border-l-transparent hover:bg-white/[0.02]";
  const rankColor = rank === 1 ? "text-[#00E5FF]" : rank === 2 ? "text-white" : "text-gray-400";
  return (
    <div className={`grid grid-cols-[40px_1fr_80px_80px] items-start gap-3 py-3 pl-3 pr-4 border-b border-white/5 transition-colors ${bg}`}
         data-testid={`mock-leader-row-${rank}`}>
      <div className={`font-display font-black text-2xl tabular-nums ${rankColor}`}>#{rank}</div>
      <div>
        <div className="text-sm text-white font-medium">{name}</div>
        <div className="text-[11px] text-gray-400 mt-1 leading-snug font-mono">{comment}</div>
      </div>
      <div className="text-right">
        <div className="font-mono text-lg text-[#00E5FF] tabular-nums leading-none">{score}</div>
        <div className="text-[9px] text-gray-500 uppercase tracking-wider mt-1">score</div>
      </div>
      <div className={`text-right font-mono text-xs tabular-nums ${delta > 0 ? "text-emerald-400" : "text-[#FF8A85]"}`}>
        {delta > 0 ? "▲" : "▼"} {Math.abs(delta).toFixed(1)}
      </div>
    </div>
  );
}

/* ─── Section wrappers ──────────────────────────────────────────── */

function SectionTag({ children }) {
  return (
    <div className="inline-flex items-center gap-2 text-[10px] font-mono text-[#00E5FF] uppercase tracking-[0.25em]">
      {children}
    </div>
  );
}

/* ─── Page ──────────────────────────────────────────────────────── */

export default function Landing() {
  const { user } = useAuth();

  return (
    <div className="industrial bg-[#0A0C10] text-white min-h-screen" data-testid="landing-page">
      <Topbar />

      {/* ═══════════════════ HERO ═══════════════════ */}
      <section className="relative overflow-hidden border-b border-white/10" data-testid="hero-section">
        <img src={HERO_BG} alt=""
             aria-hidden="true"
             className="absolute inset-0 w-full h-full object-cover opacity-25 pointer-events-none select-none" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0C10]/70 via-[#0A0C10]/85 to-[#0A0C10]" />
        <div className="absolute inset-0 bp-grid opacity-40 pointer-events-none" />

        <div className="relative max-w-6xl mx-auto px-6 md:px-10 pt-20 md:pt-28 pb-20 md:pb-24">
          <SectionTag>// EEngDeS · v3</SectionTag>

          <h1 className="mt-6 font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95] max-w-4xl"
              data-testid="hero-headline">
            Where clients meet <span className="text-[#00E5FF]">licensed engineers</span><br className="hidden md:block" />
            and code-compliant designs ship.
          </h1>

          <p className="mt-6 text-base sm:text-lg text-gray-300 max-w-2xl leading-relaxed">
            EEngDeS is the two-sided engineering marketplace + design studio.
            Post a job, review proposals, track milestones, and download stamped
            deliverables, all in one place. For engineers: bid smarter, deliver faster,
            get paid on approval.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-3" data-testid="hero-ctas">
            {user ? (
              <Link to="/dashboard" className="btn-primary inline-flex items-center gap-2 px-6 py-3"
                    data-testid="hero-continue-cta">
                Continue to dashboard <ArrowRight size={18} weight="bold" />
              </Link>
            ) : (
              <>
                <Link to="/register" className="btn-primary inline-flex items-center gap-2 px-6 py-3"
                      data-testid="hero-post-job-cta">
                  <Briefcase size={18} weight="bold" /> Post a job
                </Link>
                <Link to="/register" className="inline-flex items-center gap-2 px-6 py-3 border border-white/20 text-white hover:border-[#00E5FF] hover:text-[#00E5FF] transition-colors"
                      data-testid="hero-apply-pe-cta">
                  <HardHat size={18} weight="bold" /> Apply as an engineer
                </Link>
                <Link to="/pe/directory" className="inline-flex items-center gap-2 px-6 py-3 text-gray-300 hover:text-white transition-colors font-mono text-sm"
                      data-testid="hero-directory-link">
                  or browse the PE directory →
                </Link>
              </>
            )}
          </div>
        </div>
      </section>

      {/* ═══════════════════ AI LEADERBOARD ═══════════════════ */}
      <section className="relative border-b border-white/10" data-testid="ai-leaderboard-section">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-12 lg:gap-16 items-start">
            <div>
              <SectionTag>// AI Proposal Leaderboard</SectionTag>
              <h2 className="mt-4 font-display text-3xl md:text-4xl font-black tracking-tighter leading-tight">
                Claude 4.6 reads every bid, so you don&apos;t have to.
              </h2>
              <p className="mt-4 text-gray-400 leading-relaxed">
                Every proposal is scored on scope completeness, budget fit, timeline
                realism, and PE experience. Diff-style commentary calls out what&apos;s
                missing so you can act in minutes, not hours.
              </p>
              <div className="mt-6 flex items-center gap-2 text-[11px] font-mono text-[#FFCC00] uppercase tracking-wider">
                <Sparkle size={14} weight="fill" /> exclusive on EEngDeS
              </div>
            </div>

            {/* Mockup card */}
            <div className="border border-white/10 bg-[#12151C]" data-testid="ai-leaderboard-mockup">
              <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-black/40">
                <div className="flex items-center gap-2">
                  <Trophy size={16} className="text-[#FFCC00]" weight="fill" />
                  <div className="font-mono text-[11px] uppercase tracking-wider text-gray-400">
                    // leaderboard · 480V MCC · pittsburgh, PA
                  </div>
                </div>
                <span className="font-mono text-[10px] text-[#00E5FF]">4 bids · live</span>
              </div>
              <LeaderRow rank={1} name="Alice Sparks, P.E. · Ontario" score="94.2" delta={5.1} top
                          comment="// scope covers all 9 line-items · arc-flash included · 6-week timeline is realistic" />
              <LeaderRow rank={2} name="Volt Systems Inc." score="87.6" delta={2.3}
                          comment="// missing seismic calcs · budget on target · SR rate is high for scope" />
              <LeaderRow rank={3} name="John Volt, P.E. · Texas" score="81.9" delta={-1.4}
                          comment="// timeline aggressive · no equipment list · strong PE track record" />
              <LeaderRow rank={4} name="Southgrid Consulting" score="72.4" delta={-3.9}
                          comment="// generic scope · budget 22% over · no license on file" />
              <div className="flex items-center justify-between px-4 py-2 border-t border-white/10 bg-black/20">
                <div className="font-mono text-[10px] text-gray-500">weighted composite · updated 2m ago</div>
                <div className="font-mono text-[10px] text-[#00E5FF]">▶ verdict → award #1</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════ DUAL WORKFLOW ═══════════════════ */}
      <section className="relative border-b border-white/10 bg-[#0A0C10]" data-testid="workflow-section">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <SectionTag>// How it works</SectionTag>
          <h2 className="mt-4 font-display text-3xl md:text-4xl font-black tracking-tighter leading-tight max-w-3xl">
            Two flows. One platform. Zero friction.
          </h2>
          <p className="mt-3 text-gray-400 max-w-2xl">
            Whether you&apos;re posting a job or bidding on one, every step is
            code-first and audit-ready.
          </p>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border border-white/10">
            {/* Client column */}
            <div className="bg-[#0A0C10]" data-testid="workflow-client-column">
              <div className="px-6 py-4 border-b border-white/10 flex items-center gap-3">
                <Briefcase size={20} className="text-[#00E5FF]" weight="fill" />
                <div>
                  <div className="font-mono text-[10px] text-[#00E5FF] uppercase tracking-wider">// CLIENT_FLOW</div>
                  <div className="font-display text-lg font-black">For Clients</div>
                </div>
              </div>
              <div className="divide-y divide-white/5">
                {CLIENT_STEPS.map((s, i) => (
                  <div key={i} className="px-6 py-5 hover:bg-[#12151C] transition-colors"
                       data-testid={`workflow-client-step-${i + 1}`}>
                    <div className="flex items-start gap-4">
                      <s.icon size={22} className="text-[#00E5FF] shrink-0 mt-1" weight="duotone" />
                      <div>
                        <div className="text-[10px] font-mono text-gray-500 uppercase tracking-wider">{s.tag}</div>
                        <div className="mt-1 font-display font-black text-lg">{s.title}</div>
                        <div className="mt-1 text-sm text-gray-400 leading-relaxed">{s.copy}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* PE column */}
            <div className="bg-[#0A0C10]" data-testid="workflow-pe-column">
              <div className="px-6 py-4 border-b border-white/10 flex items-center gap-3">
                <HardHat size={20} className="text-[#FFCC00]" weight="fill" />
                <div>
                  <div className="font-mono text-[10px] text-[#FFCC00] uppercase tracking-wider">// PE_FLOW</div>
                  <div className="font-display text-lg font-black">For Engineers</div>
                </div>
              </div>
              <div className="divide-y divide-white/5">
                {PE_STEPS.map((s, i) => (
                  <div key={i} className="px-6 py-5 hover:bg-[#12151C] transition-colors"
                       data-testid={`workflow-pe-step-${i + 1}`}>
                    <div className="flex items-start gap-4">
                      <s.icon size={22} className="text-[#FFCC00] shrink-0 mt-1" weight="duotone" />
                      <div>
                        <div className="text-[10px] font-mono text-gray-500 uppercase tracking-wider">{s.tag}</div>
                        <div className="mt-1 font-display font-black text-lg">{s.title}</div>
                        <div className="mt-1 text-sm text-gray-400 leading-relaxed">{s.copy}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════ TRUST & COMPLIANCE ═══════════════════ */}
      <section className="relative border-b border-white/10 bg-[#0A0C10]" data-testid="trust-section">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <SectionTag>// Trust &amp; compliance</SectionTag>
          <h2 className="mt-4 font-display text-3xl md:text-4xl font-black tracking-tighter leading-tight max-w-3xl">
            Payment protection. License compliance.<br className="hidden md:block" />
            Signed off, on the record.
          </h2>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border border-white/10"
               data-testid="trust-grid">
            {TRUST.map((t, i) => (
              <div key={i} className="bg-[#0A0C10] p-6 md:p-8 hover:bg-[#12151C] transition-colors"
                   data-testid={`trust-card-${i + 1}`}>
                <div className="flex items-start gap-4">
                  <t.icon size={32} className="text-[#00E5FF] shrink-0" weight="duotone" />
                  <div>
                    <div className="font-display font-black text-xl tracking-tight">{t.title}</div>
                    <div className="mt-2 text-sm text-gray-400 leading-relaxed">{t.copy}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Detail row */}
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10"
               data-testid="trust-details">
            <div className="bg-[#0A0C10] p-4">
              <div className="flex items-center gap-2 font-mono text-[10px] text-[#00E5FF] uppercase tracking-wider">
                <Clock size={14} /> countdown widgets
              </div>
              <div className="mt-2 text-sm text-gray-300 leading-relaxed">
                Every submitted milestone shows a live 7-day timer on both dashboards,
                shifting grey, gold, red as it counts down.
              </div>
            </div>
            <div className="bg-[#0A0C10] p-4">
              <div className="flex items-center gap-2 font-mono text-[10px] text-[#00E5FF] uppercase tracking-wider">
                <ShieldCheck size={14} /> auto-suspend on lapse
              </div>
              <div className="mt-2 text-sm text-gray-300 leading-relaxed">
                Expired PE licenses trigger a soft suspension: new bids blocked,
                affected clients notified in-app.
              </div>
            </div>
            <div className="bg-[#0A0C10] p-4">
              <div className="flex items-center gap-2 font-mono text-[10px] text-[#00E5FF] uppercase tracking-wider">
                <FilePdf size={14} /> stamped PDFs
              </div>
              <div className="mt-2 text-sm text-gray-300 leading-relaxed">
                Proposals + deliverables export as revision-tracked PDFs, ready for
                the record file.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════ FOOTER CTA ═══════════════════ */}
      <section className="relative overflow-hidden" data-testid="footer-cta-section">
        <div className="absolute inset-0 bp-grid-strong opacity-30 pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-6 md:px-10 py-24 md:py-32">
          <div className="max-w-4xl">
            <SectionTag>// Ready to ship</SectionTag>
            <h2 className="mt-6 font-display text-4xl sm:text-5xl md:text-6xl font-black tracking-tighter leading-[0.95]"
                data-testid="footer-cta-headline">
              Post the job.<br />
              Get the design.<br />
              <span className="text-[#00E5FF]">Stamp it.</span>
            </h2>
            <p className="mt-6 text-lg text-gray-400 max-w-xl leading-relaxed">
              Free to browse the PE directory. Free to post jobs. Bidding is
              open to any admin-approved, license-verified engineer.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-3" data-testid="footer-ctas">
              <Link to="/register" className="btn-primary inline-flex items-center gap-2 px-8 py-4 text-base"
                    data-testid="footer-register-cta">
                Create your account <ArrowRight size={20} weight="bold" />
              </Link>
              <Link to="/login" className="inline-flex items-center gap-2 px-8 py-4 border border-white/20 text-white hover:border-[#00E5FF] hover:text-[#00E5FF] transition-colors"
                    data-testid="footer-login-cta">
                Sign in
              </Link>
            </div>
          </div>
        </div>

        {/* v3.2.42 · "Get in touch" moved below the "Ready to ship" pitch
             so the sign-up CTA leads and the contact form is the last
             conversion opportunity before the site footer. */}
        <ContactSection />

        <div className="border-t border-white/10 bg-black/40">
          <div className="max-w-6xl mx-auto px-6 md:px-10 py-6 flex flex-wrap items-center justify-between gap-4">
            <div className="font-mono text-[10px] text-gray-500 uppercase tracking-[0.25em]">
              EEngDeS · Enhanced Engineering Design Studio · v3
            </div>
            <div className="flex items-center gap-6 font-mono text-[10px] text-gray-500 uppercase tracking-wider">
              <Link to="/pe/directory" className="hover:text-[#00E5FF] transition-colors" data-testid="footer-directory-link">PE directory</Link>
              <Link to="/login" className="hover:text-[#00E5FF] transition-colors" data-testid="footer-signin-link">Sign in</Link>
              <Link to="/register" className="hover:text-[#00E5FF] transition-colors" data-testid="footer-register-link">Register</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
